// src/services/claudeApi.js

// ── プロキシ経由で呼び出す ──
const PROXY_URL = '/api/claude';
const CLAUDE_MODEL = 'claude-sonnet-4-20250514';

/**
 * AI Assistant - multi-turn conversation for order/stock/sales operations.
 * Returns structured actions + human-readable reply.
 */
export async function chatWithAssistant({ messages, systemPrompt }) {
  const response = await fetch(PROXY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: CLAUDE_MODEL,
      max_tokens: 4096,
      system: systemPrompt,
      messages,
    }),
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(errData.error?.message || `API error: ${response.status}`);
  }

  const data = await response.json();

  if (data.error) {
    throw new Error(data.error.message || JSON.stringify(data.error));
  }

  if (!data.content || !data.content[0]?.text) {
    throw new Error(`Unexpected API response: ${JSON.stringify(data).slice(0, 200)}`);
  }

  return data.content[0].text;
}

/**
 * Recognize multiple wine bottles in a single image (batch mode).
 * Returns an array of recognized wines.
 *
 * @param {object} params
 * @param {string} params.base64Image - Base64-encoded image data (without data URI prefix)
 * @param {string} params.mediaType  - MIME type of the image (e.g. 'image/jpeg')
 * @returns {Promise<Array<{type:string, winery:string, wineryCode:string, name:string, vintage:string, region:string, grape:string}>>}
 */
export async function recognizeMultipleWines({ base64Image, mediaType }) {
  const response = await fetch(PROXY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: CLAUDE_MODEL,
      max_tokens: 2048,
      messages: [{
        role: 'user',
        content: [
          {
            type: 'image',
            source: { type: 'base64', media_type: mediaType, data: base64Image },
          },
          {
            type: 'text',
            text: `この写真に写っている全てのワインボトルのラベルを分析してください（最大4本まで）。
以下のJSON配列形式で回答してください：
[
  {
    "type": "R/W/S/SP/P/O (Red/White/Sparkling/Champagne/Rosé/Orange)",
    "winery": "ワイナリー名（フルネーム）",
    "wineryCode": "2-3文字の略称（例：LB, DRC, CDY）",
    "name": "ワイン名（ワイナリー名を除く）",
    "vintage": "ヴィンテージ（数字のみ、例：2020）",
    "region": "産地",
    "grape": "ブドウ品種"
  }
]
写真に1本しか写っていなくても、必ず配列形式で返してください。
JSON配列のみ回答してください。`,
          },
        ],
      }],
    }),
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(errData.error?.message || `API error: ${response.status}`);
  }

  const data = await response.json();

  if (data.error) {
    throw new Error(data.error.message || 'API Error');
  }

  if (!data.content || !data.content[0]?.text) {
    throw new Error(`Unexpected API response: ${JSON.stringify(data).slice(0, 200)}`);
  }

  const text = data.content[0].text;
  const jsonMatch = text.match(/\[[\s\S]*\]/);
  if (jsonMatch) {
    return JSON.parse(jsonMatch[0]);
  }
  // Fallback: try single object
  const objMatch = text.match(/\{[\s\S]*\}/);
  if (objMatch) {
    return [JSON.parse(objMatch[0])];
  }
  throw new Error('認識結果を解析できませんでした');
}
